import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Progress, Spin } from 'antd';
import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import './managerialCrossGeneral.css';
import './AreaReport.css';
import ManCrossPageFooter from './components/ManCrossPageFooter';
import { downloadPdf, openPdf } from '../utils/pdfGenerator';
import { getMeasurementDate, getMonthYearLabel } from './shared/managerialUtils';

const PUBLIC = process.env.PUBLIC_URL || '';

// ── Color helpers ──────────────────────────────────────────────────────────────

const IVC_CFGS = [
    { nivel: 3, label: 'IVC NIV.3', levelLabel: 'RIESGO CRÍTICO',  bg: '#E74C3C', text: '#fff',    criteria: 'IPRA < 65 + IASH < 60 + NPS < 0' },
    { nivel: 2, label: 'IVC NIV.2', levelLabel: 'RIESGO ALTO',     bg: '#E67E22', text: '#fff',    criteria: 'IPRA < 65 + IASH < 60' },
    { nivel: 1, label: 'IVC NIV.1', levelLabel: 'RIESGO MODERADO', bg: '#D4AC0D', text: '#1a1a1a', criteria: 'IPRA < 70 ó IASH < 65' },
    { nivel: 0, label: 'SIN IVC',   levelLabel: 'RIESGO BAJO',     bg: '#62C190', text: '#fff',    criteria: 'Sin co-falla activa' },
];
const getIvcCfg = (n) => IVC_CFGS.find((c) => c.nivel === (n ?? 0)) ?? IVC_CFGS[3];

const ipraColor = (v) => {
    if (v == null) return '#9AA2AE';
    if (v < 65)    return '#E74C3C';
    if (v < 75)    return '#E67E22';
    if (v < 85)    return '#D4AC0D';
    return              '#62C190';
};
const iashColor = (v) => {
    if (v == null) return '#9AA2AE';
    if (v < 50)    return '#E74C3C';
    if (v < 65)    return '#E67E22';
    if (v < 80)    return '#D4AC0D';
    return              '#62C190';
};
const npsColor = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '#9AA2AE';
    if (n < 0)   return '#E74C3C';
    if (n < 20)  return '#E67E22';
    if (n < 50)  return '#D4AC0D';
    return            '#62C190';
};
const irhColor = (v) => {
    if (v == null) return '#9AA2AE';
    if (v < 50)    return '#E74C3C';
    if (v < 60)    return '#E67E22';
    if (v < 75)    return '#D4AC0D';
    return              '#62C190';
};

const fmt1 = (v) =>
    v == null || !Number.isFinite(Number(v)) ? '—' : String(Math.round(Number(v) * 10) / 10);

const fmtNps = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return n > 0 ? `+${Math.round(n)}` : String(Math.round(n));
};

const GRUPO_CFGS = {
    A:     { bg: '#E74C3C', text: '#fff',    nombre: 'Grupo A — Físico / Conductual',    desc: 'El área opera con fatiga acumulada, hábitos inseguros normalizados y déficit en protocolos de seguridad. El riesgo está en el cuerpo del operario y en la rutina diaria.' },
    B:     { bg: '#E67E22', text: '#fff',    nombre: 'Grupo B — Cognitivo / Emocional',  desc: 'El área enfrenta estrés crónico, toma de decisiones bajo presión y comunicación deficiente del riesgo. El accidente no es físico — es cognitivo.' },
    C:     { bg: '#6C4BA6', text: '#fff',    nombre: 'Grupo C — Social / Cultural',      desc: 'El área presenta bajo reporte, aislamiento y desconexión con el propósito de seguridad. El riesgo se incuba en el silencio organizacional.' },
    MIXTO: { bg: '#617993', text: '#fff',    nombre: 'Grupo MIXTO — Plan Combinado',     desc: 'El área no tiene un perfil de riesgo único dominante. El plan de intervención debe ser diferenciado según el perfil individual de cada trabajador.' },
};
const getGrupoCfg = (g) => GRUPO_CFGS[(g || '').toUpperCase()] ?? GRUPO_CFGS.MIXTO;

const COFAILURE_MODELS = [
    { key: 'd1',     label: 'D1 < 60',       subtitle: 'solo Bienestar Físico',       desc: 'Fatiga, trabaja lesionado, ignora señales corporales',        mult: 2.3, bg: '#E74C3C' },
    { key: 'd5',     label: 'D5 < 60',       subtitle: 'solo Disciplina',             desc: 'Omite EPP, saltea pasos, normaliza desvíos',                  mult: 1.9, bg: '#E74C3C' },
    { key: 'dual',   label: 'D1+D5 < 60',    subtitle: 'Co-falla crítica',            desc: 'Perfil de máxima vulnerabilidad — intervención inmediata',    mult: 3.1, bg: '#E74C3C' },
    { key: 'd2',     label: 'D2 < 60',       subtitle: 'solo Manejo Emocional',       desc: 'Trabaja bajo estrés sin regulación, impulsividad',            mult: 1.6, bg: '#E67E22' },
    { key: 'd4',     label: 'D4 < 60',       subtitle: 'solo Comunicación',           desc: 'No reporta condiciones peligrosas, silencia alertas',         mult: 1.4, bg: '#F48732' },
    { key: 'triple', label: 'D1+D2+D5 < 60', subtitle: 'Triple co-falla',             desc: 'Perfil emergencia — revisión clínica + intervención urgente', mult: 4.2, bg: '#C0392B' },
];

const PROFILE_ORDER = ['Fatigado', 'Impulsivo', 'Indisciplinado', 'Silencioso', 'Aislado', 'Distraído', 'Desconectado'];
const PROFILE_COLORS = {
    'Fatigado':       '#C0392B',
    'Impulsivo':      '#E74C3C',
    'Indisciplinado': '#E67E22',
    'Silencioso':     '#F48732',
    'Aislado':        '#F9A825',
    'Distraído':      '#D4AC0D',
    'Desconectado':   '#1E8449',
};

// ── Shared page primitives ─────────────────────────────────────────────────────

const PageHeader = ({ headerText, pageNumber }) => (
    <div className="mgcr-page-header">
        <span className="mgcr-page-header-text">{headerText}</span>
        <span className="mgcr-page-header-num">{pageNumber}</span>
    </div>
);

const ExpoConfig = (exp) => {
    const e = (exp || '').toUpperCase();
    if (e === 'EXTREMA') return { bg: '#E74C3C', text: '#fff' };
    if (e === 'ALTA')    return { bg: '#E67E22', text: '#fff' };
    if (e === 'MEDIA')   return { bg: '#D4AC0D', text: '#1a1a1a' };
    return                      { bg: '#62C190', text: '#fff' };
};

// ── IRH scatter geometry (mirrors ManCrossPageIRHScatter) ──────────────────────

const VW = 440; const VH = 200;
const PL = 26; const PR = 8; const PT = 18; const PB = 16;
const IW = VW - PL - PR;
const IH = VH - PT - PB;
const X_MIN = 30; const X_MAX = 100;
const Y_MIN = 20; const Y_MAX = 100;
const toX = (v) => PL + (Math.min(X_MAX, Math.max(X_MIN, v)) - X_MIN) / (X_MAX - X_MIN) * IW;
const toY = (v) => PT + (Math.min(Y_MAX, Math.max(Y_MIN, v)) - Y_MIN) / (Y_MAX - Y_MIN) * IH;
const TX = toX(65);
const TY = toY(60);
const DOT_COLOR = { 1: '#E74C3C', 2: '#D4AC0D', 3: '#E67E22', 4: '#62C190' };

// ── S01 — Portada ─────────────────────────────────────────────────────────────

const ArCover = ({ areaName, orgLogo, orgName, period, ivcNivel, ivcLabel }) => {
    const cfg = getIvcCfg(ivcNivel);
    return (
        <div className="ar-cover">
            <div className="ar-cover-accent" />
            <div className="ar-cover-brand">
                <img src={`${PUBLIC}/talentha-blanco.png`} alt="Talentha" className="ar-cover-logo" />
            </div>
            <div className="ar-cover-body">
                <div className="ar-cover-tag">Sistema Predictivo de Seguridad Talentha®</div>
                <div className="ar-cover-report-type">Informe de Área</div>
                <div className="ar-cover-divider" />
                <div className="ar-cover-area-name">{areaName}</div>
                <div className="ar-cover-org-row">
                    {orgLogo && <img src={orgLogo} alt="logo org" className="ar-cover-org-logo" />}
                    {orgName && <span className="ar-cover-org-name">{orgName}</span>}
                    {orgName && period && <span className="ar-cover-meta-sep">|</span>}
                    {period  && <span className="ar-cover-period">{period}</span>}
                </div>
            </div>
            <div className="ar-cover-bottom">
                <span className="ar-cover-ivc-badge" style={{ background: cfg.bg, color: cfg.text }}>
                    {cfg.label} · {ivcLabel || cfg.levelLabel}
                </span>
                <span className="ar-cover-pill">
                    Identificando y previniendo el próximo accidente grave en esta área.
                </span>
            </div>
        </div>
    );
};

// ── S02 — Resumen Ejecutivo ────────────────────────────────────────────────────

const ArKpiPage = ({ areaName, headerText, pageNumber, kpis, irhData, supervisors }) => {
    const { ipra, iash, nps, icp, ivcNivel, ivcLabel, headcount, irhAvg } = kpis;
    const cfg = getIvcCfg(ivcNivel);

    const q1Total  = irhData?.total_q1 ?? 0;
    const irhTotal = irhData?.quadrant_counts
        ? Object.values(irhData.quadrant_counts).reduce((s, n) => s + n, 0)
        : 0;
    const q1Pct = irhTotal > 0 ? Math.round(q1Total / irhTotal * 100) : 0;

    const ivcDesc = {
        3: 'Las tres barreras del sistema de seguridad están comprometidas simultáneamente en esta área.',
        2: 'Liderazgo y autocuidado presentan co-falla activa. El riesgo se multiplica.',
        1: 'Uno de los indicadores está por debajo del umbral de riesgo. Riesgo moderado activo.',
        0: 'Esta área no presenta co-falla activa — mantener y monitorear.',
    };

    const maxMult = ivcNivel >= 3
        ? { val: '4.2×', desc: 'Triple co-falla activa' }
        : ivcNivel >= 2
            ? { val: '3.1×', desc: 'Co-falla D1 + D5' }
            : ivcNivel >= 1
                ? { val: '2.3×', desc: 'Riesgo D1 aislado' }
                : { val: '1.0×', desc: 'Sin co-falla activa' };

    const kpiCards = [
        { label: 'ICP Área',         value: icp,    color: ipraColor(icp),   fmt: fmt1,   sub: icp    != null ? (icp    < 65 ? 'RIESGO CRÍTICO' : icp    < 75 ? 'RIESGO ALTO' : 'ACEPTABLE') : '—' },
        { label: 'IPRA Liderazgo',   value: ipra,   color: ipraColor(ipra),  fmt: fmt1,   sub: ipra   != null ? (ipra   < 65 ? 'RIESGO CRÍTICO' : ipra   < 75 ? 'RIESGO ALTO' : 'ACEPTABLE') : '—' },
        { label: 'IASH Autocuidado', value: iash,   color: iashColor(iash),  fmt: fmt1,   sub: iash   != null ? (iash   < 50 ? 'RIESGO CRÍTICO' : iash   < 65 ? 'RIESGO ALTO' : 'ACEPTABLE') : '—' },
        { label: 'IRH',              value: irhAvg, color: irhColor(irhAvg), fmt: fmt1,   sub: irhAvg != null ? (irhAvg < 50 ? 'RIESGO CRÍTICO' : irhAvg < 65 ? 'RIESGO ALTO' : 'MODERADO')  : '—' },
        { label: 'NPS Área',         value: nps,    color: npsColor(nps),    fmt: fmtNps, sub: nps    != null ? (nps    < 0  ? 'DETRACTOR'      : nps    < 20 ? 'NEUTRO'       : 'PROMOTOR')  : '—' },
    ];

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div style={{ marginBottom: 8 }}>
                <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1a1a2e', margin: 0, lineHeight: 1.1 }}>
                    Área: <span style={{ color: '#432b73' }}>{areaName}</span>
                </h2>
                <p style={{ fontSize: 10, color: '#617993', fontStyle: 'italic', margin: '2px 0 0' }}>
                    Estado del sistema de seguridad humana
                </p>
            </div>

            {/* 5 KPI full-color cards */}
            <div className="ar-kpi-full-row">
                {kpiCards.map(({ label, value, color, fmt, sub }) => (
                    <div key={label} className="ar-kpi-full-card" style={{ background: value != null ? color : '#9AA2AE' }}>
                        <div className="ar-kpi-full-label">{label}</div>
                        <div className="ar-kpi-full-value">{value != null ? fmt(value) : '—'}</div>
                        <div className="ar-kpi-full-sub">{sub}</div>
                    </div>
                ))}
            </div>

            {/* IVC Banner */}
            <div className="ar-ivc-banner" style={{ background: cfg.bg }}>
                <div className="ar-ivc-banner-level">{cfg.label} — {ivcLabel || cfg.levelLabel}</div>
                <div className="ar-ivc-banner-desc">{ivcDesc[ivcNivel ?? 0] ?? ivcDesc[0]}</div>
            </div>

            {/* 4 Operational stats */}
            <div className="ar-op-stats">
                <div className="ar-op-stat">
                    <div className="ar-op-stat-label">Operarios en medición</div>
                    <div className="ar-op-stat-value" style={{ color: '#432b73' }}>{headcount ?? '—'}</div>
                    <div className="ar-op-stat-sub">Registrados en esta área</div>
                </div>
                <div className="ar-op-stat">
                    <div className="ar-op-stat-label">Supervisores evaluados</div>
                    <div className="ar-op-stat-value" style={{ color: '#432b73' }}>{(supervisors || []).length || '—'}</div>
                    <div className="ar-op-stat-sub">Con IPRA activo</div>
                </div>
                <div className="ar-op-stat">
                    <div className="ar-op-stat-label">Operarios en Q1 del IRH</div>
                    <div className="ar-op-stat-value" style={{ color: '#E74C3C' }}>{q1Total}</div>
                    <div className="ar-op-stat-sub">{q1Pct}% del área</div>
                </div>
                <div className="ar-op-stat">
                    <div className="ar-op-stat-label">Multiplicador máximo</div>
                    <div className="ar-op-stat-value" style={{ color: cfg.bg }}>{maxMult.val}</div>
                    <div className="ar-op-stat-sub">{maxMult.desc}</div>
                </div>
            </div>

            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S03 — Mapa de Calor Perfiles × Dimensiones IASH ───────────────────────────

const DIM_SHORT = {
    D1: ['Bienestar', 'Físico'],
    D2: ['Manejo', 'Emocional'],
    D3: ['Conciencia', 'Personal'],
    D4: ['Comunicación', 'Riesgos'],
    D5: ['Disciplina', 'Hábitos'],
    D6: ['Responsabilidad', 'Equipo'],
    D7: ['Propósito', 'Motivación'],
};
const DIM_ORDER_HM = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7'];

const iashCellBg = (v) => {
    if (v == null) return '#f4f8fe';
    if (v < 50)    return '#fce8e8';
    if (v < 65)    return '#fdf0e2';
    if (v < 80)    return '#fdf8de';
    return              '#e8f6ef';
};

const HM_LEGEND = [
    { bg: '#fce8e8', color: '#E74C3C', label: '< 50 CRÍTICO'  },
    { bg: '#fdf0e2', color: '#E67E22', label: '50–64 ALTO'    },
    { bg: '#fdf8de', color: '#D4AC0D', label: '65–79 MODERADO' },
    { bg: '#e8f6ef', color: '#1E8449', label: '≥ 80 ÓPTIMO'   },
];

const ArHeatmapPage = ({ areaName, headerText, pageNumber, heatmapData }) => {
    const activeProfiles = useMemo(
        () => PROFILE_ORDER.filter((p) => heatmapData[p] && Object.keys(heatmapData[p]).length > 0),
        [heatmapData],
    );
    const activeDims = useMemo(() => {
        const seen = new Set();
        Object.values(heatmapData).forEach((pd) => Object.keys(pd).forEach((d) => seen.add(d)));
        return DIM_ORDER_HM.filter((d) => seen.has(d));
    }, [heatmapData]);

    if (activeProfiles.length === 0 || activeDims.length === 0) {
        return (
            <div className="ar-page">
                <PageHeader headerText={headerText} pageNumber={pageNumber} />
                <div className="keep-blankspace" />
                <div className="mgcr-dx-title-area">
                    <h2 className="mgcr-dx-main-title">Mapa de Calor — Perfiles × Dimensiones IASH</h2>
                    <p className="mgcr-dx-subtitle">{areaName} · Sin datos suficientes</p>
                </div>
                <div className="ar-callout" style={{ marginTop: 20 }}>
                    No hay operarios con perfiles de riesgo activo (score &lt; 50) en esta área en la medición actual.
                    El mapa de calor se generará cuando existan datos de perfiles.
                </div>
                <div className="keep-blankspace" />
                <ManCrossPageFooter />
            </div>
        );
    }

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div style={{ marginBottom: 8 }}>
                <h2 style={{ fontSize: 16, fontWeight: 900, color: '#1a1a2e', margin: 0, lineHeight: 1.15 }}>
                    Mapa de Calor — Perfiles × Dimensiones IASH
                </h2>
                <p style={{ fontSize: 10, color: '#617993', fontStyle: 'italic', margin: '2px 0 0' }}>
                    {areaName} · Score promedio IASH por perfil de riesgo activo y dimensión
                </p>
            </div>

            <table className="ar-hm-table">
                <thead>
                    <tr>
                        <th className="ar-hm-profile-header">Perfil</th>
                        {activeDims.map((dc) => (
                            <th key={dc} className="ar-hm-dim-header">
                                {(DIM_SHORT[dc] || [dc]).map((line, i, arr) => (
                                    <React.Fragment key={i}>
                                        {line}{i < arr.length - 1 && <br />}
                                    </React.Fragment>
                                ))}
                            </th>
                        ))}
                        <th className="ar-hm-dim-header ar-hm-avg-header">Prom.</th>
                    </tr>
                </thead>
                <tbody>
                    {activeProfiles.map((profile) => {
                        const pd     = heatmapData[profile] || {};
                        const scores = activeDims.map((dc) => pd[dc]).filter((v) => v != null);
                        const avg    = scores.length > 0
                            ? scores.reduce((s, v) => s + v, 0) / scores.length
                            : null;
                        return (
                            <tr key={profile}>
                                <td className="ar-hm-profile-cell"
                                    style={{ color: PROFILE_COLORS[profile] || '#617993' }}>
                                    {profile}
                                </td>
                                {activeDims.map((dc) => {
                                    const score = pd[dc];
                                    return (
                                        <td key={dc} className="ar-hm-cell"
                                            style={{ background: iashCellBg(score) }}>
                                            <span style={{ color: iashColor(score), fontWeight: 700 }}>
                                                {score != null ? fmt1(score) : '—'}
                                            </span>
                                        </td>
                                    );
                                })}
                                <td className="ar-hm-cell ar-hm-avg-cell"
                                    style={{ background: iashCellBg(avg) }}>
                                    <span style={{ color: iashColor(avg), fontWeight: 900 }}>
                                        {avg != null ? fmt1(avg) : '—'}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Color legend */}
            <div className="ar-hm-legend">
                {HM_LEGEND.map(({ bg, color, label }) => (
                    <div key={label} className="ar-hm-legend-item">
                        <div className="ar-hm-legend-swatch" style={{ background: bg, borderColor: color }} />
                        <span style={{ color, fontWeight: 700 }}>{label}</span>
                    </div>
                ))}
            </div>

            <div className="ar-callout" style={{ fontSize: 10, marginTop: 6 }}>
                Las celdas en rojo intenso indican los nodos de mayor vulnerabilidad combinada en el área.
                Son el punto de partida del plan diferenciado (slides 13–15).
            </div>

            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S04 — Diagnóstico del Área (4 preguntas clave) ─────────────────────────────

const ArDiagnosticPage = ({ areaName, headerText, pageNumber, ipra, iash, nps, icp, ivcNivel, ivcLabel, supervisors, irhData }) => {
    const cfg = getIvcCfg(ivcNivel);

    const q1Total  = irhData?.total_q1 ?? 0;
    const irhTotal = irhData?.quadrant_counts
        ? Object.values(irhData.quadrant_counts).reduce((s, n) => s + n, 0)
        : 0;
    const q1Pct = irhTotal > 0 ? Math.round(q1Total / irhTotal * 100) : 0;

    const criticalSups = (supervisors || []).filter((s) => (s.ipra_score ?? 100) < 65);
    const topCritDim   = criticalSups.length > 0
        ? ([...criticalSups].sort((a, b) => (a.ipra_score ?? 100) - (b.ipra_score ?? 100))[0]?.critical_dimension || null)
        : null;
    const maxMult = ivcNivel >= 3 ? 4.2 : ivcNivel >= 2 ? 3.1 : ivcNivel >= 1 ? 2.3 : 1.0;

    const questions = [
        {
            color: '#E74C3C',
            q: '¿Dónde está el riesgo?',
            body: `El área de ${areaName} presenta ${cfg.label} — ${ivcLabel || cfg.levelLabel}. IPRA = ${ipra != null ? fmt1(ipra) : '—'} · IASH = ${iash != null ? fmt1(iash) : '—'} · NPS = ${nps != null ? fmtNps(nps) : '—'} · ICP = ${icp != null ? fmt1(icp) : '—'}. Los indicadores del sistema de seguridad humana revelan vulnerabilidad activa.`,
        },
        {
            color: '#E67E22',
            q: '¿Por qué ocurre?',
            body: criticalSups.length > 0
                ? `${criticalSups.length} supervisor${criticalSups.length > 1 ? 'es' : ''} del área ${criticalSups.length > 1 ? 'tienen' : 'tiene'} IPRA < 65${topCritDim ? ` con brecha crítica en "${topCritDim}"` : ''}. Sus operarios concentran el ${q1Pct}% del déficit de autocuidado — el liderazgo es el factor amplificador principal.`
                : `El área no tiene supervisores en zona crítica de IPRA, pero ${q1Total} operario${q1Total !== 1 ? 's están' : ' está'} en Q1 del IRH. El déficit es conductual y debe atenderse con intervención de hábitos.`,
        },
        {
            color: '#E74C3C',
            q: '¿Qué pasa si no actúo?',
            body: `Con ICP = ${icp != null ? fmt1(icp) : '—'} y NPS = ${nps != null ? fmtNps(nps) : '—'}, la probabilidad de incidente grave es ${maxMult}× mayor que en áreas sin co-falla activa. ${q1Total > 0 ? `Los ${q1Total} operarios en Q1 son la población de intervención inmediata. Cada semana sin acción aumenta la exposición acumulada del área.` : 'Mantener el monitoreo activo para sostener los índices en zona segura.'}`,
        },
        {
            color: '#1E8449',
            q: '¿Qué hago esta semana?',
            body: `1. Reunión individual con ${criticalSups.length > 0 ? `los ${criticalSups.length} supervisores en zona crítica de IPRA` : 'todos los supervisores del área'} — revisar su plan de liderazgo operativo. 2. Identificar y hacer seguimiento diario a los ${q1Total} operarios de Q1. 3. Activar primer módulo del plan diferenciado (ver slides 13–15).`,
        },
    ];

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div style={{ marginBottom: 6 }}>
                <h2 style={{ fontSize: 16, fontWeight: 900, color: '#1a1a2e', margin: 0, lineHeight: 1.15 }}>
                    Diagnóstico del Área — 4 Preguntas Clave
                </h2>
                <p style={{ fontSize: 10, color: '#617993', fontStyle: 'italic', margin: '2px 0 0' }}>{areaName}</p>
            </div>
            {/* IVC header bar */}
            <div className="ar-diag-header" style={{ background: cfg.bg }}>
                <div className="ar-diag-header-level">{cfg.label} — {ivcLabel || cfg.levelLabel}</div>
                <div className="ar-diag-header-kpis">
                    IPRA = {ipra != null ? fmt1(ipra) : '—'} · IASH = {iash != null ? fmt1(iash) : '—'} · NPS = {nps != null ? fmtNps(nps) : '—'} · ICP = {icp != null ? fmt1(icp) : '—'}
                </div>
            </div>
            {/* 2×2 question grid */}
            <div className="ar-diag-grid">
                {questions.map(({ color, q, body }) => (
                    <div key={q} className="ar-diag-card">
                        <div className="ar-diag-q-head">
                            <span className="ar-diag-q-dot" style={{ background: color }} />
                            <span className="ar-diag-q-title" style={{ color }}>{q}</span>
                        </div>
                        <div className="ar-diag-body" style={{ borderLeftColor: color }}>{body}</div>
                    </div>
                ))}
            </div>
            <ManCrossPageFooter />
        </div>
    );
};

// ── S04 / S06 — Dimension bar chart ───────────────────────────────────────────

const DimBarChart = ({ rows, threshold, instrument }) => {
    if (!rows.length) {
        return <p style={{ color: '#9AA2AE', fontSize: 11, padding: '16px 0' }}>Sin datos de dimensión para esta área</p>;
    }
    const colorFn = instrument === 'ipra' ? ipraColor : iashColor;
    const thrPct  = (threshold / 100) * 100;
    return (
        <div className="ar-dim-rows">
            {rows.map((r) => {
                const scorePct = Math.min(100, Math.max(0, r.average || 0));
                const color    = colorFn(r.average);
                return (
                    <div key={r.key || r.dimension} className="ar-dim-row">
                        <div className="ar-dim-label" title={r.dimension}>{r.dimension}</div>
                        <div className="ar-dim-track">
                            <div className="ar-dim-fill" style={{ width: `${scorePct}%`, background: color }} />
                            <div className="ar-dim-threshold" style={{ left: `${thrPct}%` }} />
                        </div>
                        <div className="ar-dim-score" style={{ color }}>{fmt1(r.average)}</div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Helpers de diagnóstico de dimensiones ────────────────────────────────────

const IASH_DESC = {
    D1: 'Trabaja lesionado, ignora señales corporales, fatiga extrema acumulada.',
    D2: 'Reacciones impulsivas ante riesgo; manejo emocional deficiente en campo.',
    D3: 'Baja conciencia situacional; no detecta condiciones peligrosas en tiempo real.',
    D4: 'No reporta incidentes ni condiciones inseguras; comunicación de riesgos inhibida.',
    D5: 'Omite EPP, saltea pasos críticos, normaliza el desvío diario.',
    D6: 'No colabora en cuidado del equipo; responsabilidad individual fragmentada.',
    D7: 'Baja motivación para regresar sano; propósito de seguridad no interiorizado.',
};

const IPRA_DESC = {
    D1: 'No aplica controles críticos antes de iniciar tareas de alto riesgo.',
    D2: 'Arranques sin verificación de condiciones; procedimientos de seguridad omitidos.',
    D3: 'Incumplimiento recurrente de reglas críticas; tolerancia al desvío normalizada.',
    D4: 'Sus supervisores no detienen trabajo ante condición insegura. El equipo lo sabe.',
    D5: 'Los checklists pre-operación no se aplican antes de arrancar.',
    D6: 'No hay canales activos para reportar incidentes o condiciones inseguras.',
    D7: 'No hay presencia de los supervisores en campo durante el turno.',
    D8: 'Gestión de fatiga insuficiente; turnos extendidos sin protocolo de control.',
};

// Calcula el estado semáforo según el gap vs umbral
const dimEstado = (score, threshold) => {
    const gap = score - threshold;
    if (gap < -20) return { label: 'CRÍTICO', bg: '#C0392B', text: '#fff' };
    if (gap < -10) return { label: 'ALTO',    bg: '#E67E22', text: '#fff' };
    if (gap <   0) return { label: 'MODERADO',bg: '#F9A825', text: '#fff' };
    return             { label: 'OK',        bg: '#1E8449', text: '#fff' };
};

// Color de celda de score (fondo suave)
const dimScoreBg = (score, threshold) => {
    const gap = score - threshold;
    if (gap < -20) return '#FDECEA';
    if (gap < -10) return '#FEF0E7';
    if (gap <   0) return '#FFFDE7';
    return '#E8F5E9';
};

// Título dinámico IASH
const iashDimTitle = (rows, threshold, areaName) => {
    const below = rows.filter((r) => r.average < threshold);
    if (!below.length) return `Sin brechas de autocuidado detectadas en ${areaName}`;
    const totalGap  = below.reduce((s, r) => s + (threshold - r.average), 0);
    const top2      = below.slice(0, 2);
    const top2Gap   = top2.reduce((s, r) => s + (threshold - r.average), 0);
    const pct       = totalGap > 0 ? Math.round((top2Gap / totalGap) * 100) : 0;
    const names     = top2.map((r) => r.dimension.split(' ').slice(0, 2).join(' '));
    if (names.length === 1) return `${names[0]} concentra el ${pct}% del déficit de autocuidado en ${areaName}`;
    return `${names[0]} y ${names[1]} concentran el ${pct}% del déficit de autocuidado en ${areaName}`;
};

// Título dinámico IPRA
const ipraDimTitle = (rows, threshold, areaName) => {
    const below = rows.filter((r) => r.average < threshold);
    if (!below.length) return `Sin brechas de liderazgo detectadas en ${areaName}`;
    const top2  = below.slice(0, 2);
    const names = top2.map((r) => r.dimension.split(' ').slice(0, 2).join(' '));
    if (names.length === 1) return `${names[0]} es la brecha de liderazgo más urgente en ${areaName}`;
    return `${names[0]} y ${names[1]} son las brechas de liderazgo más urgentes en ${areaName}`;
};

// Texto de footer dinámico
const dimFooterText = (rows, threshold, instrument, areaName) => {
    const below = rows.filter((r) => r.average < threshold);
    if (!below.length) return `No se registran brechas en ${areaName} — mantener monitoreo periódico.`;
    const top = below[0];
    const shortName = top.dimension.split(' ').slice(0, 2).join(' ');
    const potential = Math.round(((threshold - top.average) / threshold) * top.weightPct);
    if (instrument === 'iash') {
        return `Mejorar ${shortName} de ${fmt1(top.average)} a ${threshold} sube el IASH del área en ~${potential} puntos — el mayor retorno por dimensión en ${areaName}.`;
    }
    return `Mejorar ${shortName} de ${fmt1(top.average)} a ${threshold} sube el IPRA del área en ~${potential} puntos — el mayor retorno por dimensión en ${areaName}.`;
};

// Componente tabla + panel de dimensiones (compartido IPRA/IASH)
const DimTablePage = ({ areaName, headerText, pageNumber, rows, threshold, instrument }) => {
    const descMap   = instrument === 'iash' ? IASH_DESC : IPRA_DESC;
    const eyebrow   = instrument === 'iash'
        ? `AUTOCUIDADO — DIMENSIONES IASH · ÁREA ${(areaName || '').toUpperCase()}`
        : `LIDERAZGO — DIMENSIONES IPRA · ÁREA ${(areaName || '').toUpperCase()}`;
    const panelTitle = instrument === 'iash' ? 'FOCO INTERVENCIÓN' : 'IMPACTO EN EL ÁREA';
    const title      = instrument === 'iash'
        ? iashDimTitle(rows, threshold, areaName)
        : ipraDimTitle(rows, threshold, areaName);
    const footerText = dimFooterText(rows, threshold, instrument, areaName);

    const below   = rows.filter((r) => r.average < threshold);
    const topItems = below.slice(0, 3);

    // Insight de co-falla (IASH únicamente)
    const insightText = (() => {
        if (instrument !== 'iash') return null;
        if (topItems.length < 2) return null;
        const k1 = topItems[0].key;
        const k2 = topItems[1].key;
        return `${k1}+${k2} simultáneos en el mismo operario elevan la probabilidad de incidente crítico. Intervención individual prioritaria.`;
    })();

    if (!rows.length) {
        return (
            <div className="ar-page">
                <PageHeader headerText={headerText} pageNumber={pageNumber} />
                <div className="ar-callout" style={{ marginTop: 20 }}>Sin datos de dimensión para esta área.</div>
                <div className="keep-blankspace" />
                <ManCrossPageFooter />
            </div>
        );
    }

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            {/* Eyebrow */}
            <div className="ar-dx-eyebrow">
                <span>{eyebrow}</span>
                <span style={{ color: '#00b7a3', fontWeight: 800 }}>TALENTHA®</span>
            </div>
            {/* Dynamic title */}
            <h2 className="ar-dx-title">{title}</h2>

            {/* Main two-column layout */}
            <div className="ar-dx-layout">
                {/* LEFT — dimension table */}
                <div className="ar-dx-table-col">
                    <table className="ar-dx-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Dimensión {instrument === 'iash' ? 'IASH' : 'IPRA'}</th>
                                <th>Peso</th>
                                <th>Resultado</th>
                                <th>Umbral</th>
                                <th>Brecha</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const gap   = Math.round(r.average - threshold);
                                const est   = dimEstado(r.average, threshold);
                                const sbg   = dimScoreBg(r.average, threshold);
                                const isCrit = gap < -10;
                                return (
                                    <tr key={r.key}>
                                        <td style={{ fontWeight: isCrit ? 700 : 400, fontSize: 9 }}>
                                            {r.key} · {r.dimension}
                                        </td>
                                        <td style={{ color: '#9AA2AE', fontSize: 9 }}>{r.weightPct}%</td>
                                        <td>
                                            <span className="ar-dx-score-cell" style={{ background: sbg, color: est.bg, padding: '2px 7px', borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                                                {fmt1(r.average)}
                                            </span>
                                        </td>
                                        <td style={{ color: '#9AA2AE', fontSize: 9 }}>{threshold}</td>
                                        <td style={{ color: gap < 0 ? est.bg : '#1E8449', fontWeight: 700, fontSize: 10 }}>
                                            {gap > 0 ? `+${gap}` : gap}
                                        </td>
                                        <td>
                                            <span className="ar-dx-estado-badge" style={{ background: est.bg }}>
                                                {est.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* RIGHT — focus/impact panel */}
                <div className="ar-dx-panel-col">
                    <div className="ar-dx-panel-title">{panelTitle}</div>
                    {topItems.map((item, idx) => (
                        <div key={item.key} className="ar-dx-focus-item">
                            <div className="ar-dx-focus-head">
                                <span className="ar-dx-focus-num">{idx + 1}</span>
                                <span className="ar-dx-focus-name">
                                    {item.dimension.split(' ').slice(0, 3).join(' ')} ({item.key})<br />
                                    <span style={{ fontWeight: 400, fontSize: 8.5, color: '#8ca0b8' }}>
                                        Resultado {fmt1(item.average)} · Peso {item.weightPct}%
                                    </span>
                                </span>
                            </div>
                            {descMap[item.key] && (
                                <div className="ar-dx-focus-desc">{descMap[item.key]}</div>
                            )}
                        </div>
                    ))}
                    {insightText && (
                        <div className="ar-dx-insight">
                            <strong>Insight:</strong> {insightText}
                        </div>
                    )}
                </div>
            </div>

            {/* Bar chart — complemento visual bajo la tabla */}
            <div className="ar-dx-chart-section">
                <div className="ar-dx-chart-label">
                    {instrument === 'iash' ? 'Score IASH por dimensión' : 'Score IPRA por dimensión'} — comparativo vs umbral
                </div>
                <DimBarChart rows={rows} threshold={threshold} instrument={instrument} />
            </div>

            {/* Teal footer callout */}
            <div className="ar-dx-footer-bar">{footerText}</div>
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

const ArDimIPRAPage = ({ areaName, headerText, pageNumber, rows }) => (
    <DimTablePage areaName={areaName} headerText={headerText} pageNumber={pageNumber}
        rows={rows} threshold={75} instrument="ipra" />
);

const ArDimIASHPage = ({ areaName, headerText, pageNumber, rows }) => (
    <DimTablePage areaName={areaName} headerText={headerText} pageNumber={pageNumber}
        rows={rows} threshold={65} instrument="iash" />
);

// ── S11 — Ranking de Supervisores ────────────────────────────────────────────

const ArSupervisorsPage = ({ areaName, headerText, pageNumber, supervisors }) => {
    const rows = supervisors.slice(0, 12);
    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Ranking de Supervisores — IPRA</h2>
                <p className="mgcr-dx-subtitle">{areaName} · Mayor riesgo primero</p>
            </div>
            {rows.length === 0 ? (
                <div className="ar-callout">Sin supervisores registrados para esta área.</div>
            ) : (
                <table className="ar-sup-table">
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>IPRA</th>
                            <th>Dimensión Crítica</th>
                            <th>N° Cargo</th>
                            <th>IVC</th>
                            <th>Exposición</th>
                            <th>Grupo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((s, i) => {
                            const ivcCfg   = getIvcCfg(s.ivc_nivel);
                            const expoCfg  = ExpoConfig(s.exposicion);
                            const grupoCfg = getGrupoCfg(s.grupo);
                            return (
                                <tr key={i}>
                                    <td style={{ fontWeight: 600 }}>{s.codigo}</td>
                                    <td>
                                        <span className="ar-badge-pill" style={{ background: ipraColor(s.ipra_score), color: '#fff' }}>
                                            {s.ipra_score != null ? fmt1(s.ipra_score) : '—'}
                                        </span>
                                    </td>
                                    <td style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {s.critical_dimension || '—'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{s.n_cargo ?? '—'}</td>
                                    <td>
                                        <span className="ar-badge-pill" style={{ background: ivcCfg.bg, color: ivcCfg.text }}>
                                            {ivcCfg.label}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="ar-badge-pill" style={{ background: expoCfg.bg, color: expoCfg.text }}>
                                            {s.exposicion || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="ar-badge-pill" style={{ background: grupoCfg.bg, color: grupoCfg.text }}>
                                            {s.grupo || '—'}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S07 — IRH Scatter del Área ────────────────────────────────────────────────

const VQS = [
    { bq: 1, label: 'Q1 CRÍTICA',    bg: '#FFE5E5', color: '#E74C3C', action: 'Intervención esta semana' },
    { bq: 3, label: 'Q2 LIDERAZGO',  bg: '#FFF0D6', color: '#E67E22', action: 'Intervenir al supervisor' },
    { bq: 2, label: 'Q3 CONDUCTUAL', bg: '#FFF8D6', color: '#D4AC0D', action: 'Plan autocuidado por perfil' },
    { bq: 4, label: 'Q4 SEGURO',     bg: '#E8F8F0', color: '#62C190', action: 'Monitoreo preventivo' },
];

const ArIrhPage = ({ areaName, headerText, pageNumber, irhData }) => {
    const scatter = irhData?.scatter || [];
    const qCounts = irhData?.quadrant_counts || {};
    const total   = scatter.length;

    const sampled = useMemo(() => {
        const q1    = scatter.filter((p) => Number(p.cuadrante) === 1);
        const other = scatter.filter((p) => Number(p.cuadrante) !== 1);
        return [...other.filter((_, i) => i % 5 === 0), ...q1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [irhData]);

    const vqStats = VQS.map((vq) => ({
        ...vq,
        count: qCounts[String(vq.bq)] ?? 0,
        pct:   total > 0 ? ((qCounts[String(vq.bq)] ?? 0) / total * 100).toFixed(0) : '0',
    }));

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">IRH — Índice de Riesgo Humano del Área</h2>
                <p className="mgcr-dx-subtitle">{areaName} · {total} operarios mapeados</p>
            </div>
            <div className="ar-scatter-wrap">
                <div className="ar-scatter-svg-box">
                    <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: 'block' }}>
                        <rect x={PL} y={PT}  width={TX - PL}       height={TY - PT}       fill="#FFE5E5" opacity="0.45" />
                        <rect x={TX} y={PT}  width={VW - PR - TX}  height={TY - PT}       fill="#FFF0D6" opacity="0.45" />
                        <rect x={PL} y={TY}  width={TX - PL}       height={VH - PB - TY}  fill="#FFF8D6" opacity="0.45" />
                        <rect x={TX} y={TY}  width={VW - PR - TX}  height={VH - PB - TY}  fill="#E8F8F0" opacity="0.45" />
                        <line x1={TX}  y1={PT}    x2={TX}    y2={VH - PB} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3" />
                        <line x1={PL}  y1={TY}    x2={VW-PR} y2={TY}      stroke="#aaa" strokeWidth="1" strokeDasharray="4,3" />
                        <text x={TX + 3} y={PT - 4} fontSize="7" fill="#9AA2AE">IPRA=65</text>
                        <text x={PL + (TX - PL) / 2} y={VH - 3} fontSize="7" fill="#E74C3C" textAnchor="middle">Q1</text>
                        <text x={(TX + VW - PR) / 2}  y={VH - 3} fontSize="7" fill="#E67E22" textAnchor="middle">Q2</text>
                        <text x={6} y={PT + (IH) / 2} fontSize="7" fill="#9AA2AE"
                            transform={`rotate(-90, 8, ${PT + IH / 2})`} textAnchor="middle">IASH</text>
                        {sampled.map((p, i) => (
                            <circle
                                key={i}
                                cx={toX(p.ipra_sup ?? 50)}
                                cy={toY(p.iash    ?? 50)}
                                r={Number(p.cuadrante) === 1 ? 4 : 2.5}
                                fill={DOT_COLOR[Number(p.cuadrante)] || '#9AA2AE'}
                                opacity={Number(p.cuadrante) === 1 ? 0.9 : 0.5}
                            />
                        ))}
                    </svg>
                </div>
                <div className="ar-scatter-legend">
                    {vqStats.map((vq) => (
                        <div key={vq.bq} className="ar-q-card"
                            style={{ background: vq.bg, borderLeftColor: vq.color }}>
                            <div className="ar-q-card-label" style={{ color: vq.color }}>{vq.label}</div>
                            <div className="ar-q-card-count" style={{ color: vq.color }}>{vq.count}</div>
                            <div style={{ fontSize: 8, color: '#617993' }}>{vq.pct}% · {vq.action}</div>
                        </div>
                    ))}
                    {irhData?.irh_avg != null && (
                        <div style={{ fontSize: 10, color: '#617993', textAlign: 'center', marginTop: 6, fontWeight: 600 }}>
                            IRH prom.: <span style={{ color: irhColor(irhData.irh_avg) }}>
                                {fmt1(irhData.irh_avg)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S08 — Top Q1 del Área ─────────────────────────────────────────────────────

const getIrhBadge = (v) => {
    if (v == null) return { bg: '#e8eef5', text: '#617993' };
    if (v < 50)    return { bg: '#E74C3C', text: '#fff' };
    if (v <= 60)   return { bg: '#E67E22', text: '#fff' };
    return                { bg: '#D4AC0D', text: '#1a1a1a' };
};

const ArTopQ1Page = ({ areaName, headerText, pageNumber, irhData }) => {
    const rows    = irhData?.top_q1  || [];
    const totalQ1 = irhData?.total_q1 ?? 0;
    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Top Operarios — Riesgo Extremo Q1</h2>
                <p className="mgcr-dx-subtitle">{areaName} · {totalQ1} operarios en zona crítica</p>
            </div>
            {totalQ1 === 0 ? (
                <div className="ar-callout">Sin operarios en Q1 (IASH &lt; 60 + IPRA sup &lt; 65) en esta área. Mantener el monitoreo activo.</div>
            ) : (
                <table className="ar-sup-table">
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>IASH</th>
                            <th>IPRA Sup</th>
                            <th>IRH</th>
                            <th>Perfil</th>
                            <th>Acción</th>
                            <th>Sup. Código</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, 12).map((r, i) => {
                            const irhBadge = getIrhBadge(r.irh);
                            return (
                                <tr key={i}>
                                    <td style={{ fontWeight: 600 }}>{r.codigo || '—'}</td>
                                    <td><span className="ar-badge-pill" style={{ background: iashColor(r.iash),    color: '#fff' }}>{r.iash    != null ? fmt1(r.iash)    : '—'}</span></td>
                                    <td><span className="ar-badge-pill" style={{ background: ipraColor(r.ipra_sup), color: '#fff' }}>{r.ipra_sup != null ? fmt1(r.ipra_sup) : '—'}</span></td>
                                    <td><span className="ar-badge-pill" style={{ background: irhBadge.bg,           color: irhBadge.text }}>{r.irh != null ? fmt1(r.irh) : '—'}</span></td>
                                    <td>{r.perfil   || '—'}</td>
                                    <td style={{ fontWeight: 600, color: '#E74C3C' }}>{r.accion || 'Intervención'}</td>
                                    <td style={{ color: '#617993' }}>{r.sup_codigo || '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S09 — Matrices Predictivas 2×2 ───────────────────────────────────────────

// Definición de cuadrantes (alineado con backend: cuadrante 1/2/3/4)
// Eje X: IPRA sup  |  Eje Y: IASH operario
// Grid visual: [Q3][Q4] (IASH ≥ 60 top) / [Q1][Q2] (IASH < 60 bottom)
const PRED_QUAD_DEFS = [
    {
        bq: 3, gridRow: 1, gridCol: 1,
        short: 'Q · LIDERAZGO',
        fullLabel: 'Riesgo de Liderazgo',
        axes: 'IPRA sup < 65 · IASH ≥ 60',
        cardBg: '#FFF8E7', accent: '#E67E22',
        headerBg: '#E67E22',
        interpretation: 'El operario cuida su comportamiento pero el supervisor no ejerce liderazgo efectivo. Riesgo silencioso que escala con el tiempo.',
        action: 'Coaching de liderazgo al supervisor. Revisión del modelo IPRA por dimensión.',
        icon: '⚠',
    },
    {
        bq: 4, gridRow: 1, gridCol: 2,
        short: 'Q · SEGURO',
        fullLabel: 'Zona Segura',
        axes: 'IPRA sup ≥ 65 · IASH ≥ 60',
        cardBg: '#EAFAF1', accent: '#1E8449',
        headerBg: '#1E8449',
        interpretation: 'Supervisor y operario alineados en prácticas seguras. Grupo de referencia del área.',
        action: 'Monitoreo preventivo. Usar como modelo interno para replicar en otros cuadrantes.',
        icon: '✓',
    },
    {
        bq: 1, gridRow: 2, gridCol: 1,
        short: 'Q · DUAL',
        fullLabel: 'Riesgo Dual — Crítico',
        axes: 'IPRA sup < 65 · IASH < 60',
        cardBg: '#FFF0F0', accent: '#C0392B',
        headerBg: '#C0392B',
        interpretation: 'El supervisor no lidera la seguridad y el operario no practica autocuidado. Máxima probabilidad de incidente crítico.',
        action: 'Intervención individual inmediata. Ambas dimensiones simultáneas — IPRA + IASH.',
        icon: '⬥',
    },
    {
        bq: 2, gridRow: 2, gridCol: 2,
        short: 'Q · CONDUCTUAL',
        fullLabel: 'Riesgo Conductual',
        axes: 'IPRA sup ≥ 65 · IASH < 60',
        cardBg: '#FFF5EC', accent: '#D4AC0D',
        headerBg: '#D4AC0D',
        interpretation: 'El supervisor mantiene estándares pero el operario muestra bajo autocuidado. Riesgo individual latente.',
        action: 'Plan IASH diferenciado por perfil de riesgo. No requiere intervención en liderazgo.',
        icon: '▲',
    },
];

const ArMatricesPage = ({ areaName, headerText, pageNumber, irhData }) => {
    const scatter   = irhData?.scatter          || [];
    const qCounts   = irhData?.quadrant_counts  || {};
    const total     = scatter.length;

    // Promedios por cuadrante (IASH promedio y IPRA_sup promedio)
    const qStats = useMemo(() => {
        const acc = { 1: [], 2: [], 3: [], 4: [] };
        scatter.forEach((p) => {
            const q = Number(p.cuadrante);
            if (acc[q]) acc[q].push(p);
        });
        const avg = (arr, key) => arr.length ? (arr.reduce((s, p) => s + (p[key] ?? 0), 0) / arr.length).toFixed(1) : '—';
        return Object.fromEntries(
            Object.entries(acc).map(([q, pts]) => [q, {
                count:    qCounts[q] ?? pts.length,
                pct:      total > 0 ? Math.round(((qCounts[q] ?? pts.length) / total) * 100) : 0,
                avgIash:  avg(pts, 'iash'),
                avgIpra:  avg(pts, 'ipra_sup'),
            }])
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [irhData]);

    // Prioridad de intervención: Q1 > Q3 > Q2 > Q4
    const priorityOrder = [1, 3, 2, 4];
    const q1Count = qStats[1]?.count ?? 0;

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />

            {/* Header */}
            <div style={{ marginBottom: 10, flexShrink: 0 }}>
                <h2 style={{ fontSize: 16, fontWeight: 900, color: '#1a1a2e', margin: 0, lineHeight: 1.2 }}>
                    Matrices Predictivas 2×2 — Cruce IPRA × IASH
                </h2>
                <p style={{ fontSize: 9.5, color: '#617993', margin: '3px 0 0', fontStyle: 'italic' }}>
                    {areaName} · {total} operarios mapeados · Umbral IPRA = 65 · Umbral IASH = 60
                </p>
            </div>

            {/* Axis labels + 2x2 grid */}
            <div className="ar-mx-container" style={{ flexShrink: 0 }}>
                {/* Y-axis label */}
                <div className="ar-mx-yaxis">
                    <span className="ar-mx-axis-label">IASH Operario</span>
                </div>
                <div className="ar-mx-body">
                    {/* X-axis header */}
                    <div className="ar-mx-xheader">
                        <div className="ar-mx-xband ar-mx-xband--left">
                            IPRA sup &lt; 65 — Liderazgo débil
                        </div>
                        <div className="ar-mx-xband ar-mx-xband--right">
                            IPRA sup ≥ 65 — Liderazgo efectivo
                        </div>
                    </div>
                    {/* Row labels + grid */}
                    <div className="ar-mx-grid-wrap">
                        {/* Row labels */}
                        <div className="ar-mx-rows-labels">
                            <div className="ar-mx-row-label ar-mx-row-label--top">
                                IASH ≥ 60
                                <span>Autocuidado OK</span>
                            </div>
                            <div className="ar-mx-row-label ar-mx-row-label--bottom">
                                IASH &lt; 60
                                <span>Riesgo conductual</span>
                            </div>
                        </div>
                        {/* 2×2 Cards */}
                        <div className="ar-mx-grid">
                            {PRED_QUAD_DEFS.map((qd) => {
                                const st = qStats[qd.bq] || { count: 0, pct: 0, avgIash: '—', avgIpra: '—' };
                                return (
                                    <div key={qd.bq} className="ar-mx-card" style={{ background: qd.cardBg, borderColor: qd.accent }}>
                                        <div className="ar-mx-card-header" style={{ background: qd.headerBg }}>
                                            <span className="ar-mx-card-icon">{qd.icon}</span>
                                            <span className="ar-mx-card-label">{qd.fullLabel}</span>
                                        </div>
                                        <div className="ar-mx-card-body">
                                            <div className="ar-mx-count" style={{ color: qd.accent }}>
                                                {st.count}
                                                <span className="ar-mx-pct">{st.pct}%</span>
                                            </div>
                                            <div className="ar-mx-axes" style={{ color: qd.accent }}>{qd.axes}</div>
                                            <div className="ar-mx-interp">{qd.interpretation}</div>
                                            <div className="ar-mx-action" style={{ borderLeftColor: qd.accent }}>
                                                {qd.action}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* X-axis label */}
                    <div className="ar-mx-xlabel">IPRA del Supervisor</div>
                </div>
            </div>

            {/* Priority strip */}
            <div className="ar-mx-priority-strip" style={{ flexShrink: 0, marginTop: 10 }}>
                <div className="ar-mx-priority-title">Orden de intervención recomendado</div>
                <div className="ar-mx-priority-row">
                    {priorityOrder.map((bq, idx) => {
                        const qd = PRED_QUAD_DEFS.find((d) => d.bq === bq);
                        const st = qStats[bq] || { count: 0, pct: 0 };
                        return (
                            <div key={bq} className="ar-mx-prio-item" style={{ borderColor: qd.accent }}>
                                <span className="ar-mx-prio-num" style={{ background: qd.accent }}>{idx + 1}</span>
                                <div className="ar-mx-prio-info">
                                    <div className="ar-mx-prio-label" style={{ color: qd.accent }}>{qd.fullLabel}</div>
                                    <div className="ar-mx-prio-count">{st.count} pers. · {st.pct}%</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Bottom callout */}
            {q1Count > 0 && (
                <div className="ar-callout" style={{ background: '#FFF0F0', borderLeft: '4px solid #C0392B', marginTop: 8, flexShrink: 0 }}>
                    <strong style={{ color: '#C0392B' }}>{q1Count} persona{q1Count !== 1 ? 's' : ''} en Riesgo Dual</strong> —
                    {' '}requieren intervención esta semana antes de continuar la jornada normal.
                </div>
            )}

            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S10 — Co-fallas IASH del Área ─────────────────────────────────────────────

const ArCofailPage = ({ areaName, headerText, pageNumber, cofailure }) => (
    <div className="ar-page">
        <PageHeader headerText={headerText} pageNumber={pageNumber} />
        <div className="keep-blankspace" />
        <div className="mgcr-dx-title-area">
            <h2 className="mgcr-dx-main-title">Modelos de Co-falla IASH del Área</h2>
            <p className="mgcr-dx-subtitle">{areaName}</p>
        </div>
        <div className="ar-cf-grid">
            {COFAILURE_MODELS.map((m) => {
                const d = cofailure[m.key];
                return (
                    <div key={m.key} className="ar-cf-card" style={{ background: m.bg }}>
                        <div className="ar-cf-card-label">{m.label}</div>
                        <span className="ar-cf-card-sub">({m.subtitle})</span>
                        <div className="ar-cf-card-mult">{m.mult}×</div>
                        <div className="ar-cf-card-more">más probable</div>
                        {d && <div className="ar-cf-card-count">{d.count} pers. ({d.pct}%)</div>}
                        <div className="ar-cf-card-desc">{m.desc}</div>
                    </div>
                );
            })}
        </div>
        <div className="ar-callout">
            El multiplicador no es estadístico — indica el aumento de probabilidad de accidente grave cuando estos patrones coexisten. Cada caso requiere intervención individual, no grupal.
        </div>
        <div className="keep-blankspace" />
        <ManCrossPageFooter />
    </div>
);

// ── S08 — Perfiles + Supervisores (combinado) ─────────────────────────────────

const ArProfilesAndSupsPage = ({ areaName, headerText, pageNumber, profiles, supervisors, grupo }) => {
    const grupoCfg = getGrupoCfg(grupo);
    const total    = profiles?.total || 0;
    const profs    = profiles?.profiles || {};

    const bars = PROFILE_ORDER
        .filter((name) => (profs[name] || 0) > 0)
        .map((name) => ({
            name,
            count: profs[name],
            pct:   total > 0 ? Math.round(profs[name] / total * 100) : 0,
            color: PROFILE_COLORS[name] || '#9AA2AE',
        }))
        .sort((a, b) => b.pct - a.pct);
    const maxPct = bars.reduce((m, b) => Math.max(m, b.pct), 1);

    const supRows    = (supervisors || []).slice(0, 6);
    const allCritical = supRows.length > 0 && supRows.every((s) => (s.ipra_score ?? 100) < 65);

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div style={{ marginBottom: 6 }}>
                <h2 style={{ fontSize: 15, fontWeight: 900, color: '#1a1a2e', margin: 0, lineHeight: 1.15 }}>
                    Perfiles de Riesgo y Supervisores del Área
                </h2>
                <p style={{ fontSize: 10, color: '#617993', fontStyle: 'italic', margin: '2px 0 0' }}>{areaName}</p>
            </div>
            <div className="ar-ps-layout">
                {/* Left: Profiles panel */}
                <div className="ar-ps-panel">
                    <div className="ar-ps-panel-header" style={{ background: '#E74C3C', color: '#fff' }}>
                        PERFILES DE OPERARIOS ({total})
                    </div>
                    <div className="ar-ps-panel-body">
                        {/* Grupo badge */}
                        <div className="ar-grupo-badge-row">
                            <div className="ar-grupo-sq" style={{ background: grupoCfg.bg }}>
                                <span className="ar-grupo-sq-letter">
                                    {(grupo || 'M').toUpperCase().charAt(0)}
                                </span>
                            </div>
                            <div>
                                <div className="ar-grupo-badge-name">{grupoCfg.nombre}</div>
                                <div className="ar-grupo-badge-sub">Perfil de intervención del área</div>
                            </div>
                        </div>
                        {bars.length === 0 ? (
                            <div style={{ color: '#9AA2AE', fontSize: 10, padding: '8px 0' }}>Sin perfiles activos</div>
                        ) : bars.map((b, idx) => (
                            <div key={b.name} className="ar-ps-profile-row">
                                <div className="ar-ps-profile-name"
                                    style={{ color: b.color, fontWeight: idx < 2 ? 800 : 500 }}>
                                    {b.name}
                                </div>
                                <div className="ar-ps-profile-track">
                                    <div style={{ height: '100%', width: `${(b.pct / maxPct) * 100}%`, background: b.color, borderRadius: 5 }} />
                                </div>
                                <div className="ar-ps-profile-pct"
                                    style={{ color: b.color, fontWeight: idx < 2 ? 800 : 500 }}>
                                    {b.pct}%
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Right: Supervisors panel */}
                <div className="ar-ps-panel">
                    <div className="ar-ps-panel-header" style={{ background: '#432b73', color: '#fff' }}>
                        PERFIL DE SUPERVISORES ({supRows.length})
                    </div>
                    <div className="ar-ps-panel-body">
                        {supRows.length === 0 ? (
                            <div style={{ color: '#9AA2AE', fontSize: 10, padding: '8px 0' }}>Sin supervisores registrados</div>
                        ) : supRows.map((s, i) => {
                            const ivcCfg  = getIvcCfg(s.ivc_nivel);
                            const borderC = (s.ipra_score ?? 100) < 65 ? '#E74C3C' : '#62C190';
                            return (
                                <div key={i} className="ar-sup-item" style={{ borderLeftColor: borderC }}>
                                    <div className="ar-sup-item-head">
                                        <span className="ar-sup-item-code">{s.codigo || `Sup-${i + 1}`}</span>
                                        <span className="ar-sup-item-ipra"
                                            style={{ background: ipraColor(s.ipra_score), color: '#fff' }}>
                                            {s.ipra_score != null ? fmt1(s.ipra_score) : '—'}
                                        </span>
                                        {s.n_cargo != null && (
                                            <span className="ar-sup-item-cargo">{s.n_cargo} a cargo</span>
                                        )}
                                    </div>
                                    {s.critical_dimension && (
                                        <div className="ar-sup-item-dim">Dim. crítica: {s.critical_dimension}</div>
                                    )}
                                    <span className="ar-sup-item-badge"
                                        style={{ background: ivcCfg.bg, color: ivcCfg.text }}>
                                        {ivcCfg.levelLabel}
                                    </span>
                                </div>
                            );
                        })}
                        {allCritical && (
                            <div className="ar-sup-alert" style={{ background: '#E74C3C' }}>
                                Los {supRows.length} supervisores del área están en zona crítica de IPRA. Intervención coordinada urgente.
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S11 — Ranking de Supervisores ────────────────────────────────────────────

const ArPlanGroupPage = ({ areaName, headerText, pageNumber, grupo, planes }) => {
    const areaPlan = planes.find((p) => (p.grupo || '').toUpperCase() === (grupo || '').toUpperCase());
    const cfg = getGrupoCfg(grupo);

    return (
        <div className="ar-page">
            <PageHeader headerText={headerText} pageNumber={pageNumber} />
            <div className="keep-blankspace" />
            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Plan Diferenciado — Grupo {(grupo || 'MIXTO').toUpperCase()}</h2>
                <p className="mgcr-dx-subtitle">{areaName}</p>
            </div>
            {!areaPlan ? (
                <div className="ar-callout">Sin plan diferenciado disponible para el grupo de esta área.</div>
            ) : (
                <>
                    <div className="ar-callout" style={{ background: cfg.bg }}>
                        {areaPlan.grupo_nombre} — Este plan emerge de los datos reales del área, no de una plantilla genérica.
                    </div>
                    <table className="ar-plan-table">
                        <thead><tr><th>Actor</th><th>Acción</th><th>Plazo</th><th>KPI</th></tr></thead>
                        <tbody>
                            {(areaPlan.acciones || []).map((a, i) => (
                                <tr key={i}>
                                    <td style={{ fontWeight: 600, color: '#432b73' }}>{a.actor}</td>
                                    <td>{a.accion}</td>
                                    <td><span className="ar-plazo-badge" style={{ background: a.plazo_color || '#617993' }}>{a.plazo}</span></td>
                                    <td style={{ color: '#617993' }}>{a.kpi}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

// ── S14 — Plan de Acción 90 Días ──────────────────────────────────────────────

const IVC_ROW_COLORS = { '-Niv.3': '#e74c3c', '-Niv.2': '#e67e22', 'Reduce expo': '#e67e22', 'Monitor.': '#18af4b' };

const ArPlan90Page = ({ areaName, headerText, pageNumber, plan90, callout90 }) => (
    <div className="ar-page">
        <PageHeader headerText={headerText} pageNumber={pageNumber} />
        <div className="keep-blankspace" />
        <div className="mgcr-dx-title-area">
            <h2 className="mgcr-dx-main-title">Plan de Acción — 90 Días</h2>
            <p className="mgcr-dx-subtitle">{areaName}</p>
        </div>
        {callout90 && <div className="ar-callout">{callout90}</div>}
        <table className="ar-plan-table">
            <thead>
                <tr>
                    <th>Acción</th><th>Población</th><th>Responsable</th>
                    <th>Semana</th><th>IVC</th><th>KPI</th>
                </tr>
            </thead>
            <tbody>
                {plan90.slice(0, 10).map((r, i) => (
                    <tr key={i}>
                        <td>{r.accion}</td>
                        <td>{r.poblacion}</td>
                        <td style={{ fontWeight: 600 }}>{r.responsable}</td>
                        <td style={{ fontWeight: 600 }}>{r.semana}</td>
                        <td><span className="ar-badge-pill" style={{ background: IVC_ROW_COLORS[r.ivc] || '#617993', color: '#fff' }}>{r.ivc || '—'}</span></td>
                        <td style={{ color: '#617993' }}>{r.kpi_exito}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <div className="keep-blankspace" />
        <ManCrossPageFooter />
    </div>
);

// ── S15 — Metas KPI a 90 Días ─────────────────────────────────────────────────

const ArKpiTargetsPage = ({ areaName, orgName, headerText, pageNumber, kpis }) => {
    const { ipra, iash, ivcNivel, ivcLabel } = kpis;
    const ivcCfg = getIvcCfg(ivcNivel);

    const today90 = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 90);
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
    })();

    const targets = [
        {
            name: 'IVC del Área',
            todayVal:   ivcCfg.label,
            todayLabel: ivcLabel || ivcCfg.levelLabel,
            metaVal:    ivcNivel > 1 ? 'Bajar a IVC Niv.1' : ivcNivel === 1 ? 'Eliminar IVC' : 'Mantener SIN IVC',
            metaLabel:  ivcNivel > 0 ? 'En 90 días' : 'Monitoreo continuo',
            border:     ivcCfg.bg,
        },
        {
            name: 'IPRA Promedio del Área',
            todayVal:   ipra != null ? fmt1(ipra) : '—',
            todayLabel: ipra != null ? (ipra < 65 ? 'RIESGO CRÍTICO' : ipra < 75 ? 'RIESGO ALTO' : 'ACEPTABLE') : '—',
            metaVal:    'Subir a ≥ 75',
            metaLabel:  'Riesgo Moderado',
            border:     ipraColor(ipra),
        },
        {
            name: 'IASH Promedio del Área',
            todayVal:   iash != null ? fmt1(iash) : '—',
            todayLabel: iash != null ? (iash < 50 ? 'RIESGO CRÍTICO' : iash < 65 ? 'RIESGO ALTO' : 'ACEPTABLE') : '—',
            metaVal:    'Subir a ≥ 55',
            metaLabel:  'Riesgo Alto reducido',
            border:     iashColor(iash),
        },
    ];

    return (
        <div className="ar-targets-page">
            <div style={{ padding: '16px 20px 4px' }}>
                <h2 style={{ fontSize: 20, fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1.15 }}>
                    Área: <span style={{ color: '#00b7a3' }}>{areaName}</span>
                </h2>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '2px 0 0' }}>
                    Metas a 90 días — próxima medición programada
                </p>
            </div>
            <div className="ar-target-list" style={{ padding: '0 20px' }}>
                {targets.map((t) => (
                    <div key={t.name} className="ar-target-row" style={{ borderLeftColor: t.border }}>
                        <div className="ar-target-kpi-name">{t.name}</div>
                        <div className="ar-target-values">
                            <div>
                                <div className="ar-target-today-label">HOY</div>
                                <div className="ar-target-today-val" style={{ color: t.border }}>{t.todayVal}</div>
                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>{t.todayLabel}</div>
                            </div>
                            <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.18)', alignSelf: 'center' }}>→</div>
                            <div>
                                <div className="ar-target-meta-label">META 90 DÍAS</div>
                                <div className="ar-target-meta-val">{t.metaVal}</div>
                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>{t.metaLabel}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="ar-targets-footer" style={{ margin: '0 20px 14px' }}>
                <div>
                    <div className="ar-targets-footer-key">Próxima medición</div>
                    <div className="ar-targets-footer-val">{today90}</div>
                </div>
                <div>
                    <div className="ar-targets-footer-key">Cómo se mide</div>
                    <div className="ar-targets-footer-val">Re-aplicación IPRA + IASH en el área</div>
                </div>
                <div>
                    <div className="ar-targets-footer-key">Responsable</div>
                    <div className="ar-targets-footer-val">{orgName || 'Líder del Área'}</div>
                </div>
                <div>
                    <div className="ar-targets-footer-key">Soporte Talentha</div>
                    <div className="ar-targets-footer-val">info@talentha.co</div>
                </div>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * AreaReport — Informe de Área (16 slides, landscape letter)
 *
 * Props:
 *   reportData  {object}        — full managerial cross report API response
 *   orgLogo     {string|null}   — base64 or URL of the client logo
 *   orgName     {string|null}   — organisation display name
 *   areaName    {string}        — area name (key used in payload.irh_by_area, etc.)
 */
const AreaReport = forwardRef(({ reportData, orgLogo, orgName, areaName }, ref) => {
    const pdfRef = useRef();
    const [pdfLoading,  setPdfLoading]  = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);

    const payload     = reportData?.payload    || {};
    const evaluations = payload?.evaluations   || {};

    const measurementDate = useMemo(
        () => getMeasurementDate(evaluations, reportData?.created_at),
        [evaluations, reportData?.created_at],
    );
    const period = useMemo(() => getMonthYearLabel(measurementDate), [measurementDate]);

    // ── Per-area data ──────────────────────────────────────────────────────────
    const ivcData     = payload.ivc_by_area?.[areaName]             || {};
    const icpData     = payload.icp_by_area?.[areaName]             || {};
    const ipraScore   = payload.ipra_avg_by_area?.[areaName]        ?? null;
    const iashScore   = payload.iash_avg_by_area?.[areaName]        ?? null;
    const irhData     = payload.irh_by_area?.[areaName]             || {};
    const cofailure   = payload.cofailure_by_area?.[areaName]       || {};
    const supervisors = payload.supervisors_by_area?.[areaName]     || [];
    const profiles    = payload.iash_profiles_by_area?.[areaName]   || {};
    const grupo       = payload.grupo_by_area?.[areaName]           || 'MIXTO';
    const heatmapData = payload.iash_heatmap_by_area?.[areaName]    || {};

    const nps = useMemo(
        () => (payload.nps_data?.by_area || []).find((r) => r.area === areaName)?.nps ?? null,
        [payload, areaName],
    );

    const headcount = useMemo(
        () => (payload.headcount_by_management_area || []).find((r) => r.name === areaName)?.headcount ?? null,
        [payload, areaName],
    );

    // ── Dimension rows: merge area scores with global dim metadata ─────────────
    const ipraDimRows = useMemo(() => {
        const areaScores = payload.ipra_by_area_and_dimension?.[areaName] || {};
        const globalMeta = payload.totals?.ipra?.average_by_dimension     || {};
        return Object.entries(globalMeta)
            .map(([key, meta]) => ({
                key,
                dimension: (meta?.title || key).split('/')[0].trim(),
                average:   areaScores[key] ?? null,
                weightPct: Math.round(meta?.weight_percent ?? (meta?.weight ?? 0) * 100),
            }))
            .filter((r) => r.average != null)
            .sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [payload, areaName]);

    const iashDimRows = useMemo(() => {
        const areaScores = payload.iash_by_area_and_dimension?.[areaName] || {};
        const globalMeta = payload.totals?.iash?.average_by_dimension     || {};
        return Object.entries(globalMeta)
            .map(([key, meta]) => ({
                key,
                dimension: (meta?.title || key).split('/')[0].trim(),
                average:   areaScores[key] ?? null,
                weightPct: Math.round(meta?.weight_percent ?? (meta?.weight ?? 0) * 100),
            }))
            .filter((r) => r.average != null)
            .sort((a, b) => (a.average ?? 999) - (b.average ?? 999));
    }, [payload, areaName]);

    // ── KPI object ────────────────────────────────────────────────────────────
    const kpis = {
        ipra:     ipraScore,
        iash:     iashScore,
        nps,
        icp:      typeof icpData?.icp === 'number' ? icpData.icp : null,
        ivcNivel: ivcData?.nivel ?? 0,
        ivcLabel: ivcData?.label ?? null,
        headcount,
        irhAvg:   irhData?.irh_avg ?? null,
    };

    // ── PDF ───────────────────────────────────────────────────────────────────
    const pdfFilename = `Informe Area ${areaName} ${evaluations?.ipra?.title || ''}.pdf`;
    const onProgress  = useCallback((c, t) => setPdfProgress({ current: c, total: t }), []);

    const handleDownloadPDF = async () => {
        setPdfLoading(true);
        await downloadPdf(pdfRef.current, { filename: pdfFilename, orientation: 'landscape', onProgress });
        setPdfLoading(false);
        setPdfProgress(null);
    };
    const handleOpenPDF = async () => {
        setPdfLoading(true);
        await openPdf(pdfRef.current, { filename: pdfFilename, orientation: 'landscape', onProgress });
        setPdfLoading(false);
        setPdfProgress(null);
    };

    useImperativeHandle(ref, () => ({
        downloadPdf:    handleDownloadPDF,
        openPdf:        handleOpenPDF,
        getPdfFilename: () => pdfFilename,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [pdfFilename]);

    const headerText = `${orgName ? `${orgName} · ` : ''}Informe de Área — ${areaName} · ${period}`;

    return (
        <div className="informe-wrapper">
            {pdfLoading && ReactDOM.createPortal(
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(135deg, rgba(62,36,107,0.93) 0%, rgba(41,55,144,0.93) 100%)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999,
                }}>
                    <div style={{
                        background: 'rgba(255,255,255,0.12)',
                        borderRadius: 24,
                        padding: '48px 56px',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 20,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        minWidth: 300,
                    }}>
                        <div style={{
                            width: 72, height: 72,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FilePdfOutlined style={{ fontSize: 36, color: '#fff' }} />
                        </div>
                        <div style={{ color: '#fff', fontSize: 18, fontWeight: 600, textAlign: 'center' }}>
                            Generando PDF
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, textAlign: 'center', lineHeight: 1.5 }}>
                            Este proceso puede tomar un momento…
                        </div>
                        {pdfProgress ? (
                            <div style={{ width: '100%', marginTop: 4 }}>
                                <Progress
                                    percent={Math.round((pdfProgress.current / pdfProgress.total) * 100)}
                                    strokeColor={{ from: '#36d1c4', to: '#5b8def' }}
                                    trailColor="rgba(255,255,255,0.15)"
                                    showInfo={false}
                                    strokeWidth={8}
                                    style={{ marginBottom: 8 }}
                                />
                                <div style={{
                                    color: 'rgba(255,255,255,0.85)', fontSize: 13,
                                    textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                                }}>
                                    Página {pdfProgress.current} de {pdfProgress.total}
                                </div>
                            </div>
                        ) : (
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: 'rgba(255,255,255,0.8)' }} spin />} />
                        )}
                    </div>
                </div>
            , document.body)}
            <div ref={pdfRef} className="pdf-content mgcr-content">

                {/* S01 — Portada */}
                <div className="pdf-page mgcr-page mgcr-page-cover">
                    <ArCover areaName={areaName} orgLogo={orgLogo} orgName={orgName}
                        period={period} ivcNivel={kpis.ivcNivel} ivcLabel={kpis.ivcLabel} />
                </div>

                {/* S02 — Resumen KPIs */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArKpiPage areaName={areaName} headerText={headerText} pageNumber={2}
                        kpis={kpis} irhData={irhData} supervisors={supervisors} />
                </div>

                {/* S03 — Mapa de Calor */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArHeatmapPage areaName={areaName} headerText={headerText} pageNumber={3}
                        heatmapData={heatmapData} />
                </div>

                {/* S04 — Diagnóstico 4 Preguntas */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArDiagnosticPage areaName={areaName} headerText={headerText} pageNumber={4}
                        ipra={ipraScore} iash={iashScore} nps={nps} icp={kpis.icp}
                        ivcNivel={kpis.ivcNivel} ivcLabel={kpis.ivcLabel}
                        supervisors={supervisors} irhData={irhData} />
                </div>

                {/* S05 — Dimensiones IPRA */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArDimIPRAPage areaName={areaName} headerText={headerText} pageNumber={5} rows={ipraDimRows} />
                </div>

                {/* S06 — Dimensiones IASH */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArDimIASHPage areaName={areaName} headerText={headerText} pageNumber={6} rows={iashDimRows} />
                </div>

                {/* S07 — IRH Scatter */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArIrhPage areaName={areaName} headerText={headerText} pageNumber={7} irhData={irhData} />
                </div>

                {/* S08 — Perfiles + Supervisores combinado */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArProfilesAndSupsPage areaName={areaName} headerText={headerText} pageNumber={8}
                        profiles={profiles} supervisors={supervisors} grupo={grupo} />
                </div>

                {/* S09 — Matrices Predictivas 2×2 */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArMatricesPage areaName={areaName} headerText={headerText} pageNumber={9} irhData={irhData} />
                </div>

                {/* S10 — Co-fallas IASH */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArCofailPage areaName={areaName} headerText={headerText} pageNumber={10} cofailure={cofailure} />
                </div>

                {/* S11 — Ranking Supervisores */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArSupervisorsPage areaName={areaName} headerText={headerText} pageNumber={11} supervisors={supervisors} />
                </div>

                {/* S12 — Top Q1 */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArTopQ1Page areaName={areaName} headerText={headerText} pageNumber={12} irhData={irhData} />
                </div>

                {/* S13 — Plan Diferenciado */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArPlanGroupPage areaName={areaName} headerText={headerText} pageNumber={13}
                        grupo={grupo} planes={payload.planes || []} />
                </div>

                {/* S14 — Plan 90 Días */}
                <div className="pdf-page mgcr-page mgcr-page-summary">
                    <ArPlan90Page areaName={areaName} headerText={headerText} pageNumber={14}
                        plan90={payload.plan_90_dias || []} callout90={payload.callout_90 || ''} />
                </div>

                {/* S15 — Metas KPI (fondo oscuro, sin padding, sin PageHeader — espejo de portada) */}
                <div className="pdf-page mgcr-page mgcr-page-cover ar-page-dark">
                    <ArKpiTargetsPage areaName={areaName} orgName={orgName} headerText={headerText} pageNumber={15} kpis={kpis} />
                </div>

            </div>
        </div>
    );
});

AreaReport.displayName = 'AreaReport';
export default AreaReport;
